export enum ShopCartType {
  Product = 'P',
  Service = 'S',
  Package = 'K',
  GiftCard = 'G',
  Class = 'E',
  Membership = 'M'
}

export type CartItemDetailsParams = {
  item_id: number;
  item_type: ShopCartType;
  qty: number;
  attendees?: number[];
  attendees_rec_id?: number[];
  recipient_clid?: number;
  store_id?: number;
};

export type PackageCartItemDetails = {
  error: boolean;
  error_message: string;
  name: string;
  dscr: string | null;
  amount: string;
  qty: number;
  amount_total: number;
  tax_amount_total: number;
  owner_id: number;
  owner_name: string | null;
  qry: number;
  amount_orig: number;
  charge_now: number;
};

export type ExtendedPackageCartItemDetails = PackageCartItemDetails & {
  id: number;
  price?: number;
  quantity: number;
};

export type CheckoutCartItem = {
  item_id: number;
  item_type: ShopCartType;
  qty?: number;
  recipient_clid?: number | null;
  store_id?: number | null;
  attendees?: number[];
};

export type CheckoutParameters = {
  exec_pymt: 0 | 1;
  membership_locid?: number;
  items_list: CheckoutCartItem[];
  class_session_id?: number;
  gcard_payments?: { gcard_id: number; amount: number }[];
  acct_id?: number;
  account_charge?: number;
  shipping_id?: number;
  addr_id?: number | null;
  sign_file?: string;
};

export interface ShoppingItemCartParams {
  invoice_id?: number;
  item_id: number;
  item_type: string;
  store_id?: number | null;
  qty: number;
  recipient_clid?: number;
}

export interface AddShoppingCartParameters extends ShoppingItemCartParams {
  invoice_id?: number;
}

export interface AddShoppingCartBulkParameters {
  invoice_id?: number;
  items_list: ShoppingItemCartParams[];
}

export interface UpdateShoppingCartParameters extends ShoppingItemCartParams {
  cart_item_id: number;
  invoice_id?: number;
}

export interface DeleteShoppingCartParameters {
  invoice_id: number;
  cart_item_id: number;
}

export type ShopCartDetails = {
  invoice_id: number;
  shipping_id: number;
  shipping_cost: number;
  tax_cost: number;
  total_cost: number;
  total_items_cost: number;
  shipping_name: string;
  shipping_is_pickup: boolean;
  shipping_addr: {
    addr1: string | null;
    addr2: string | null;
    addr_id: number;
    city: string | null;
    country: string | null;
    name: string | null;
    phone: string | null;
    pickup_store_id: number;
    pickup_store_name: string | null;
    state: string | null;
    zip: string | null;
  };
  balance: number;
  store_id: number;
  client_data: {
    email: string;
    givname: string;
    id: number;
    name: string;
  };
  is_pickup: number | null;
};

export enum CartItemOnlineFulfillmentEnum {
  ShippingOnly = 0,
  ShippingAndPickup = 1,
  PickupOnly = 2
}

export type ShopCartItem = {
  cart_item_id: number;
  rel_cart_item_id: number;
  id: number;
  item_type: ShopCartType;
  name: string;
  dscr: string;
  brand_id: number;
  brand_name: string;
  amount: number;
  base_discount: number;
  base_price: number;
  qty: number;
  is_promo: number;
  owner_id: number;
  owner_name: string;
  amount_total: number;
  size: string;
  img: string;
  is_electronic: number;
  is_shippable: number;
  online_fulfillment: CartItemOnlineFulfillmentEnum;
  store_id: number;
};

export type CartBasketItem = {
  id: number;
  name: string;
  oldItemPrice: number;
  newItemPrice: number;
  priceWithDiscount: number;
  totalWithAllDiscount: number;
  qty: number;
  size?: string;
  img?: string;
  online_fulfillment?: CartItemOnlineFulfillmentEnum;
};

export type ShopCartPayments = {
  amount: number;
  gcard_id: number;
  giftcard_number: string;
  in_pymt_id: number;
  is_gift_card: number;
  type: string;
  acct_id: number;
};

export interface ShoppingCartResponse {
  details:
    | ShopCartDetails
    | {
        invoice_id: number | null;
        total_cost: number;
        shipping_cost: number | null;
        tax_cost: number;
        total_items_cost: number;
        shipping_id: number | null;
        is_pickup: number | null;
        shipping_addr: {
          pickup_store_id: number | null;
          addr_id: number | null;
        };
      };
  items: ShopCartItem[];
  payments: ShopCartPayments[];
}

export interface SuccessResponse {
  success: boolean;
}

export interface ShopCartUpdateResponse extends SuccessResponse {
  cart_list: ShoppingCartResponse;
  error?: boolean;
}

export enum ShopCartStep {
  DETAILS,
  DELIVERY,
  CHECKOUT,
  SUCCESS
}

export type ShopPromoParamsType = {
  invoice_id: number;
  promo_code: string;
};

export type ShopPromoResponseType = {
  success: boolean;
  invoice_id: number;
  promo_id: number;
  promo_name: string;
  promo_dscr: string;
  code?: string;
  error?: string;
  error_code?: number;
};

export type CartShippingResponseType = {
  id: number;
  name: string;
  cost: number;
};

export enum ShipmentModeType {
  NO_MODE,
  PICKUP,
  SHIPMENT,
  ADD_ADDRESS
}

export type AddShippingMethodParameters = {
  invoice_id: number;
  shipping_id: number;
  addr_id?: number;
  store_id?: number;
  cart_item_id_arr?: number[];
};

export type AddGiftCardMethodParameters = {
  invoice_id: number;
  gcard_id: number;
  serial?: string;
  amount: number;
};

export type RemoveGiftCardMethodParameters = {
  invoice_id: number;
  in_pymt_id: number;
};

export type AddCreditCardMethodParameters = {
  invoice_id: number;
  acct_id: number;
  in_pymt_id?: number;
};

export type RemoveCreditCardMethodParameters = {
  invoice_id: number;
  in_pymt_id: number;
};

export type CheckoutResponse = {
  approval_code: string;
  error: string;
  exec_pymt: number;
  invoice_id: number;
  success: boolean;
  sys_error: string;
  invoice_num?: number;
  invoice_link_num?: number;
};

export type CheckoutCreationResponse = {
  exec_pymt: 0;
  amount_paid: 0;
  success?: boolean;
  invoice_num: number;
  invoice_link_num: number | null;
  error?: string;
  sys_error?: string;
};

export type LocalCartData = ShoppingCartResponse;

export enum UserFulfillmentMode {
  Ship = 'ship',
  Pickup = 'pickup'
}

export interface FulfillmentPreference {
  cart_item_id: number;
  original_fulfillment: CartItemOnlineFulfillmentEnum;
  user_selected: UserFulfillmentMode;
  timestamp: number;
}
